import { useCallback, useMemo, useState } from 'react';
import {
  DuplicationMatchResult,
  PublisherReviewStatus,
} from '@amzn/awscat-pattern-library-service-model';

import { useToolbarActionService } from '../../contexts/ToolbarActionServiceContext';
import { DuplicationResultsState } from '../../services/PublisherReviewService';
import { usePublisherReviewState } from '../v2-hooks/usePublisherReviewState';
import { useWorkflow } from '../v2-hooks/useWorkflow';
import { useLoadedContentId } from '../v2-hooks/useLoadedContentId';
import {
  DUPLICATION_SCANNER_NAME,
  DuplicationFindingState,
  ScannerFindingsSummary,
  computeScannerFindings,
  getTopDuplicationMatches,
} from '../../services/ScannerFindings';
import { DuplicateScanResultItem } from '../duplicate-scan-results/DuplicateScanResultsView';
import { recordPublisherReviewDuplicationClickthrough } from '../../monitoring/PublisherReviewTelemetry';

export type BarRaiserReviewSubState = 'inProcess' | 'rejected';

export interface BarRaiserStatePanelViewModel {
  readonly isScanning: boolean;
  readonly isLoadingFindings: boolean;
  /** True when the publisher review scan failed or timed out, so the view can surface it. */
  readonly isScanFailed: boolean;
  readonly scannerFindings: readonly ScannerFindingsSummary[];
  readonly subState: BarRaiserReviewSubState;
  readonly openEvaluationPanel: () => void;
  /** Switches the sidebar to the comments panel so the reviewer can triage scan comments. */
  readonly openCommentsPanel: () => void;
  /** Whether the duplication-results modal is open. */
  readonly duplicationModalVisible: boolean;
  /** Top-5 flagged duplication matches shown in the modal, mapped for the shared scan-results view. */
  readonly duplicationModalMatches: readonly DuplicateScanResultItem[];
  readonly openDuplicationModal: () => void;
  readonly dismissDuplicationModal: () => void;
  /** Records a clickthrough when the reviewer opens a flagged duplicate match. */
  readonly onDuplicationResultClick: (matchedContentId: string) => void;
}

/** Maps the service's duplication state to the duplication row's display state. */
function toDuplicationFindingState(
  duplicationResults: DuplicationResultsState | undefined,
): DuplicationFindingState | undefined {
  if (!duplicationResults) return undefined;
  if (duplicationResults.status === 'failed') return 'failed';
  return getTopDuplicationMatches(duplicationResults.matches).length > 0
    ? 'hasResults'
    : 'noResults';
}

/**
 * Maps a duplication match to the shared scan-results item. The bar raiser
 * endpoint provides only id/title/url/score, so source and reason are left
 * undefined and the card hides those rows.
 */
function toScanResultItem(match: DuplicationMatchResult): DuplicateScanResultItem {
  return {
    id: match.matchedContentId,
    title: match.matchedContentTitle,
    url: match.matchedContentUrl,
    similarityMatch: match.similarityScore,
  };
}

export function useBarRaiserStatePanelViewModel(): BarRaiserStatePanelViewModel {
  const toolbarService = useToolbarActionService();
  const workflow = useWorkflow();
  const contentId = useLoadedContentId();
  const { state: scanState, results: scanResults } = usePublisherReviewState({
    workflow,
    contentId,
  });

  const [duplicationModalVisible, setDuplicationModalVisible] = useState(false);

  const openEvaluationPanel = useCallback(
    () => (toolbarService.activePanel = 'bar-raiser-evaluation'),
    [toolbarService],
  );

  const openCommentsPanel = useCallback(
    () => (toolbarService.activePanel = 'comments'),
    [toolbarService],
  );

  const openDuplicationModal = useCallback(() => setDuplicationModalVisible(true), []);
  const dismissDuplicationModal = useCallback(() => setDuplicationModalVisible(false), []);

  const isScanning = scanState === 'scan_in_progress';
  const isLoadingFindings = scanState === 'loading_results';

  // A failed or timed-out scan produces no findings, so the findings table alone
  // reads as "no issues". The bar raiser has no separate timeout treatment (and
  // cannot rescan), so both outcomes flag the same manual-review warning.
  const isScanFailed = scanResults?.status === 'failed' || scanResults?.status === 'timed_out';

  // When scan results arrive, decorate the duplication row with its endpoint-driven state.
  const scannerFindings = useMemo(() => {
    if (!scanResults) return [];
    const findings = computeScannerFindings({
      inlineEdits: [],
      scanComments: scanResults.scanComments ?? [],
      scanErrors: [],
      stage: 'publisherReview',
    });
    const duplicationState = toDuplicationFindingState(scanResults.duplicationResults);
    return findings.map((finding) =>
      finding.scannerName === DUPLICATION_SCANNER_NAME ? { ...finding, duplicationState } : finding,
    );
  }, [scanResults]);

  const duplicationModalMatches = useMemo(
    () =>
      scanResults?.duplicationResults.status === 'ok'
        ? getTopDuplicationMatches(scanResults.duplicationResults.matches).map(toScanResultItem)
        : [],
    [scanResults],
  );

  // When the reviewer opens a flagged match, record a clickthrough for RUM.
  const scanId = workflow?.publisherReviewInfo?.scanId;
  const onDuplicationResultClick = useCallback(
    (matchedContentId: string) => {
      if (!contentId || !scanId) return;
      recordPublisherReviewDuplicationClickthrough({ contentId, scanId, matchedContentId });
    },
    [contentId, scanId],
  );

  const reviewStatus = workflow?.publisherReviewInfo?.reviewStatus;
  const subState: BarRaiserReviewSubState =
    reviewStatus === PublisherReviewStatus.REJECTED ? 'rejected' : 'inProcess';

  return {
    isScanning,
    isLoadingFindings,
    isScanFailed,
    scannerFindings,
    subState,
    openEvaluationPanel,
    openCommentsPanel,
    duplicationModalVisible,
    duplicationModalMatches,
    openDuplicationModal,
    dismissDuplicationModal,
    onDuplicationResultClick,
  };
}
